import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, Plus, Trash2, Search, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { refreshSeo, type SeoSettings } from "@/components/SeoHead";

const defaultSeo: SeoSettings = {
  siteTitle: "StoreSEO AI - AI-powered SEO for e-commerce",
  titleTemplate: "%s | StoreSEO AI",
  description:
    "Generate high-converting, SEO-optimized product titles, descriptions, keywords and tags in seconds with AI.",
  keywords: "seo, ecommerce seo, product seo, ai seo tool, shopify seo",
  ogImage: "",
  twitterHandle: "",
  canonicalBase: "",
  jsonLd: "",
  pages: {},
};

const defaultRobots = `User-agent: *\nAllow: /\n`;

export default function AdminSeo() {
  const [seo, setSeo] = useState<SeoSettings>(defaultSeo);
  const [robotsTxt, setRobotsTxt] = useState<string>(defaultRobots);
  const [sitemapUrls, setSitemapUrls] = useState<string[]>([
    "/",
    "/about",
    "/terms",
    "/refund-policy",
    "/privacy",
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditUrl, setAuditUrl] = useState("");
  const [auditing, setAuditing] = useState(false);
  const [audit, setAudit] = useState<any>(null);
  const [newPagePath, setNewPagePath] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "seo")
        .maybeSingle();
      const v = (data?.value as any) || {};
      setSeo({ ...defaultSeo, ...v, pages: { ...defaultSeo.pages, ...(v.pages || {}) } });
      if (v.robotsTxt) setRobotsTxt(v.robotsTxt);
      if (Array.isArray(v.sitemapUrls) && v.sitemapUrls.length) setSitemapUrls(v.sitemapUrls);
      if (!seo.canonicalBase && typeof window !== "undefined") {
        setSeo((prev) => ({ ...prev, canonicalBase: v.canonicalBase || window.location.origin }));
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    const value = { ...seo, robotsTxt, sitemapUrls };
    const { error } = await supabase
      .from("app_settings")
      .upsert({ key: "seo", value: value as any }, { onConflict: "key" });
    setSaving(false);
    if (error) return toast.error("Failed to save", { description: error.message });
    await refreshSeo();
    toast.success("SEO settings saved");
  };

  const runAudit = async () => {
    if (!auditUrl) return toast.error("Enter a URL");
    setAuditing(true);
    setAudit(null);
    try {
      const { data, error } = await supabase.functions.invoke("seo-audit", {
        body: { url: auditUrl },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setAudit(data.audit);
    } catch (e: any) {
      toast.error("Audit failed", { description: e.message });
    } finally {
      setAuditing(false);
    }
  };

  const filesBase = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/seo-files`;

  if (loading) return <Skeleton className="h-64 w-full" />;

  const pageEntries = Object.entries(seo.pages || {});

  return (
    <Tabs defaultValue="meta" className="space-y-4">
      <TabsList className="w-full flex-wrap h-auto justify-start gap-1">
        <TabsTrigger value="meta">Meta tags</TabsTrigger>
        <TabsTrigger value="pages">Per-page</TabsTrigger>
        <TabsTrigger value="schema">JSON-LD</TabsTrigger>
        <TabsTrigger value="files">robots & sitemap</TabsTrigger>
        <TabsTrigger value="audit">Audit</TabsTrigger>
      </TabsList>

      <TabsContent value="meta">
        <Card>
          <CardHeader>
            <CardTitle>Site-wide meta tags</CardTitle>
            <CardDescription>Defaults used on every page.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 max-w-2xl">
            <div className="space-y-2">
              <Label>Default title</Label>
              <Input value={seo.siteTitle} onChange={(e) => setSeo({ ...seo, siteTitle: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Title template <span className="text-xs text-muted-foreground">(use %s for page title)</span></Label>
              <Input value={seo.titleTemplate} onChange={(e) => setSeo({ ...seo, titleTemplate: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Meta description</Label>
              <Textarea rows={3} value={seo.description} onChange={(e) => setSeo({ ...seo, description: e.target.value })} />
              <p className="text-xs text-muted-foreground">{seo.description.length}/160 characters</p>
            </div>
            <div className="space-y-2">
              <Label>Keywords (comma separated)</Label>
              <Input value={seo.keywords} onChange={(e) => setSeo({ ...seo, keywords: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>OG image URL</Label>
              <Input placeholder="https://..." value={seo.ogImage} onChange={(e) => setSeo({ ...seo, ogImage: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Twitter handle</Label>
                <Input placeholder="@yourhandle" value={seo.twitterHandle} onChange={(e) => setSeo({ ...seo, twitterHandle: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Canonical base URL</Label>
                <Input placeholder="https://www.storeseoai.com" value={seo.canonicalBase} onChange={(e) => setSeo({ ...seo, canonicalBase: e.target.value })} />
              </div>
            </div>
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="pages">
        <Card>
          <CardHeader>
            <CardTitle>Per-page overrides</CardTitle>
            <CardDescription>Override title/description/keywords for specific routes.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 max-w-2xl">
            <div className="flex gap-2">
              <Input placeholder="/about" value={newPagePath} onChange={(e) => setNewPagePath(e.target.value)} />
              <Button
                variant="outline"
                onClick={() => {
                  if (!newPagePath.startsWith("/")) return toast.error("Path must start with /");
                  setSeo({ ...seo, pages: { ...seo.pages, [newPagePath]: {} } });
                  setNewPagePath("");
                }}
              >
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>

            {pageEntries.length === 0 && (
              <p className="text-sm text-muted-foreground">No overrides yet.</p>
            )}

            {pageEntries.map(([path, cfg]) => (
              <div key={path} className="rounded-lg border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="secondary">{path}</Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const next = { ...seo.pages };
                      delete next[path];
                      setSeo({ ...seo, pages: next });
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Input
                  placeholder="Title"
                  value={cfg.title || ""}
                  onChange={(e) =>
                    setSeo({ ...seo, pages: { ...seo.pages, [path]: { ...cfg, title: e.target.value } } })
                  }
                />
                <Textarea
                  placeholder="Description"
                  rows={2}
                  value={cfg.description || ""}
                  onChange={(e) =>
                    setSeo({ ...seo, pages: { ...seo.pages, [path]: { ...cfg, description: e.target.value } } })
                  }
                />
                <Input
                  placeholder="Keywords"
                  value={cfg.keywords || ""}
                  onChange={(e) =>
                    setSeo({ ...seo, pages: { ...seo.pages, [path]: { ...cfg, keywords: e.target.value } } })
                  }
                />
                <Input
                  placeholder="OG image URL"
                  value={cfg.ogImage || ""}
                  onChange={(e) =>
                    setSeo({ ...seo, pages: { ...seo.pages, [path]: { ...cfg, ogImage: e.target.value } } })
                  }
                />
              </div>
            ))}

            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="schema">
        <Card>
          <CardHeader>
            <CardTitle>JSON-LD structured data</CardTitle>
            <CardDescription>Injected on every page inside &lt;script type="application/ld+json"&gt;.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 max-w-3xl">
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setSeo({
                    ...seo,
                    jsonLd: JSON.stringify(
                      {
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        name: "StoreSEO AI",
                        url: seo.canonicalBase || "https://www.storeseoai.com",
                        logo: seo.ogImage,
                      },
                      null,
                      2
                    ),
                  })
                }
              >
                Organization template
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setSeo({
                    ...seo,
                    jsonLd: JSON.stringify(
                      {
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        name: "StoreSEO AI",
                        url: seo.canonicalBase || "https://www.storeseoai.com",
                      },
                      null,
                      2
                    ),
                  })
                }
              >
                WebSite template
              </Button>
            </div>
            <Textarea
              rows={14}
              className="font-mono text-xs"
              placeholder='{ "@context": "https://schema.org", "@type": "Organization", ... }'
              value={seo.jsonLd}
              onChange={(e) => setSeo({ ...seo, jsonLd: e.target.value })}
            />
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="files">
        <Card>
          <CardHeader>
            <CardTitle>robots.txt & sitemap.xml</CardTitle>
            <CardDescription>
              Served from your backend. Point your domain (or a redirect) to these URLs.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5 max-w-3xl">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <code className="px-2 py-1 rounded bg-muted text-xs">{filesBase}?file=robots.txt</code>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(`${filesBase}?file=robots.txt`); toast.success("Copied"); }}><Copy className="h-3.5 w-3.5" /></Button>
                <a href={`${filesBase}?file=robots.txt`} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost"><ExternalLink className="h-3.5 w-3.5" /></Button></a>
              </div>
              <div className="flex items-center gap-2">
                <code className="px-2 py-1 rounded bg-muted text-xs">{filesBase}?file=sitemap.xml</code>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(`${filesBase}?file=sitemap.xml`); toast.success("Copied"); }}><Copy className="h-3.5 w-3.5" /></Button>
                <a href={`${filesBase}?file=sitemap.xml`} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost"><ExternalLink className="h-3.5 w-3.5" /></Button></a>
              </div>
            </div>

            <div className="space-y-2">
              <Label>robots.txt</Label>
              <Textarea rows={6} className="font-mono text-xs" value={robotsTxt} onChange={(e) => setRobotsTxt(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>Sitemap URLs (one per line, paths or full URLs)</Label>
              <Textarea
                rows={8}
                className="font-mono text-xs"
                value={sitemapUrls.join("\n")}
                onChange={(e) => setSitemapUrls(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
              />
            </div>

            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="audit">
        <Card>
          <CardHeader>
            <CardTitle>SEO audit</CardTitle>
            <CardDescription>Fetch a URL and grade it with AI.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 max-w-2xl">
            <div className="flex gap-2">
              <Input placeholder="https://www.storeseoai.com" value={auditUrl} onChange={(e) => setAuditUrl(e.target.value)} />
              <Button onClick={runAudit} disabled={auditing}>
                {auditing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Audit
              </Button>
            </div>

            {audit && (
              <div className="space-y-3 rounded-lg border p-4">
                <div className="flex items-center gap-3">
                  <div className="text-3xl font-bold">{audit.score}</div>
                  <div className="text-sm text-muted-foreground">/100</div>
                </div>
                <div className="text-sm"><b>Title:</b> {audit.title}</div>
                <div className="text-sm"><b>Description:</b> {audit.meta_description}</div>
                <div className="text-sm flex flex-wrap gap-2">
                  <Badge variant={audit.has_canonical ? "default" : "destructive"}>Canonical</Badge>
                  <Badge variant={audit.has_og_tags ? "default" : "destructive"}>OG tags</Badge>
                  <Badge variant={audit.has_structured_data ? "default" : "destructive"}>JSON-LD</Badge>
                  <Badge variant="secondary">H1: {audit.h1_count}</Badge>
                </div>
                {audit.top_recommendations?.length > 0 && (
                  <div>
                    <div className="font-medium text-sm mb-1">Top recommendations</div>
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                      {audit.top_recommendations.map((r: string, i: number) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
